import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface AddActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonId: string;
  nurseryId: string;
  sortOrder: number;
}

type ActivityType = "multiple_choice" | "drag_drop" | "ordering" | "fill_blank";

const ACTIVITY_LABELS: Record<ActivityType, string> = {
  multiple_choice: "اختيار من متعدد",
  drag_drop: "سحب وإفلات",
  ordering: "ترتيب",
  fill_blank: "أكمل الفراغ",
};

const AddActivityDialog = ({ open, onOpenChange, lessonId, nurseryId, sortOrder }: AddActivityDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ActivityType>("multiple_choice");
  const [maxScore, setMaxScore] = useState(10);

  // Multiple choice state
  const [mcQuestion, setMcQuestion] = useState("");
  const [mcOptions, setMcOptions] = useState([
    { id: "a", text: "" },
    { id: "b", text: "" },
    { id: "c", text: "" },
    { id: "d", text: "" },
  ]);
  const [mcCorrectId, setMcCorrectId] = useState("a");

  // Drag drop state
  const [ddQuestion, setDdQuestion] = useState("");
  const [ddItems, setDdItems] = useState([{ id: "1", text: "" }]);
  const [ddZones, setDdZones] = useState([{ id: "z1", label: "", correctItemId: "1" }]);

  // Ordering state
  const [ordQuestion, setOrdQuestion] = useState("");
  const [ordItems, setOrdItems] = useState([{ id: "1", text: "" }]);

  // Fill blank state
  const [fbQuestion, setFbQuestion] = useState("");
  const [fbCorrectAnswer, setFbCorrectAnswer] = useState("");
  const [fbHint, setFbHint] = useState("");

  const resetForm = () => {
    setTitle("");
    setType("multiple_choice");
    setMaxScore(10);
    setMcQuestion("");
    setMcOptions([{ id: "a", text: "" }, { id: "b", text: "" }, { id: "c", text: "" }, { id: "d", text: "" }]);
    setMcCorrectId("a");
    setDdQuestion("");
    setDdItems([{ id: "1", text: "" }]);
    setDdZones([{ id: "z1", label: "", correctItemId: "1" }]);
    setOrdQuestion("");
    setOrdItems([{ id: "1", text: "" }]);
    setFbQuestion("");
    setFbCorrectAnswer("");
    setFbHint("");
  };

  const buildContent = () => {
    switch (type) {
      case "multiple_choice":
        return { question: mcQuestion, options: mcOptions, correctId: mcCorrectId };
      case "drag_drop":
        return { question: ddQuestion, items: ddItems, zones: ddZones };
      case "ordering":
        return { question: ordQuestion, items: ordItems, correctOrder: ordItems.map(i => i.id) };
      case "fill_blank":
        return { question: fbQuestion, correctAnswer: fbCorrectAnswer, hint: fbHint };
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ title: "أدخل عنوان النشاط", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from("lesson_activities").insert({
        lesson_id: lessonId,
        nursery_id: nurseryId,
        title,
        activity_type: type,
        content: buildContent(),
        sort_order: sortOrder,
        max_score: maxScore,
      });
      if (error) throw error;
      toast({ title: "تم إضافة النشاط بنجاح ✅" });
      queryClient.invalidateQueries({ queryKey: ["lesson-activities", lessonId] });
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة نشاط تفاعلي</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>عنوان النشاط</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: تمرين الطرح" />
            </div>
            <div className="space-y-2">
              <Label>نوع النشاط</Label>
              <Select value={type} onValueChange={(v) => setType(v as ActivityType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTIVITY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>الدرجة القصوى</Label>
            <Input type="number" value={maxScore} onChange={(e) => setMaxScore(Number(e.target.value))} min={1} className="w-32" />
          </div>

          <div className="border rounded-lg p-4 space-y-4 bg-muted/20">
            <Badge variant="outline">{ACTIVITY_LABELS[type]}</Badge>

            {/* Multiple Choice */}
            {type === "multiple_choice" && (
              <>
                <div className="space-y-2">
                  <Label>السؤال</Label>
                  <Textarea value={mcQuestion} onChange={(e) => setMcQuestion(e.target.value)} placeholder="اكتب السؤال..." />
                </div>
                <div className="space-y-2">
                  <Label>الخيارات</Label>
                  {mcOptions.map((opt, i) => (
                    <div key={opt.id} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correct"
                        checked={mcCorrectId === opt.id}
                        onChange={() => setMcCorrectId(opt.id)}
                        className="accent-primary"
                      />
                      <Input
                        value={opt.text}
                        onChange={(e) => {
                          const updated = [...mcOptions];
                          updated[i].text = e.target.value;
                          setMcOptions(updated);
                        }}
                        placeholder={`الخيار ${i + 1}`}
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">حدد الإجابة الصحيحة بالضغط على الدائرة</p>
                </div>
              </>
            )}

            {/* Drag and Drop */}
            {type === "drag_drop" && (
              <>
                <div className="space-y-2">
                  <Label>السؤال</Label>
                  <Textarea value={ddQuestion} onChange={(e) => setDdQuestion(e.target.value)} placeholder="مثال: اسحب الرقم إلى المكان الصحيح" />
                </div>
                <div className="space-y-2">
                  <Label>العناصر (القطع القابلة للسحب)</Label>
                  {ddItems.map((item, i) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Input
                        value={item.text}
                        onChange={(e) => {
                          const updated = [...ddItems];
                          updated[i].text = e.target.value;
                          setDdItems(updated);
                        }}
                        placeholder={`عنصر ${i + 1}`}
                      />
                      {ddItems.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => setDdItems(ddItems.filter((_, j) => j !== i))}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setDdItems([...ddItems, { id: String(ddItems.length + 1), text: "" }])}>
                    <Plus className="w-3 h-3 ml-1" /> إضافة عنصر
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>المناطق (أماكن الإفلات)</Label>
                  {ddZones.map((zone, i) => (
                    <div key={zone.id} className="flex items-center gap-2">
                      <Input
                        value={zone.label}
                        onChange={(e) => {
                          const updated = [...ddZones];
                          updated[i].label = e.target.value;
                          setDdZones(updated);
                        }}
                        placeholder={`منطقة ${i + 1}`}
                        className="flex-1"
                      />
                      <Select
                        value={zone.correctItemId}
                        onValueChange={(v) => {
                          const updated = [...ddZones];
                          updated[i].correctItemId = v;
                          setDdZones(updated);
                        }}
                      >
                        <SelectTrigger className="w-32"><SelectValue placeholder="العنصر الصحيح" /></SelectTrigger>
                        <SelectContent>
                          {ddItems.map(item => (
                            <SelectItem key={item.id} value={item.id}>{item.text || `عنصر ${item.id}`}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {ddZones.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => setDdZones(ddZones.filter((_, j) => j !== i))}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setDdZones([...ddZones, { id: `z${ddZones.length + 1}`, label: "", correctItemId: ddItems[0]?.id || "1" }])}>
                    <Plus className="w-3 h-3 ml-1" /> إضافة منطقة
                  </Button>
                </div>
              </>
            )}

            {/* Ordering */}
            {type === "ordering" && (
              <>
                <div className="space-y-2">
                  <Label>السؤال</Label>
                  <Textarea value={ordQuestion} onChange={(e) => setOrdQuestion(e.target.value)} placeholder="مثال: رتب الأرقام من الأصغر إلى الأكبر" />
                </div>
                <div className="space-y-2">
                  <Label>العناصر (بالترتيب الصحيح)</Label>
                  {ordItems.map((item, i) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Badge variant="outline" className="w-8 h-8 flex items-center justify-center shrink-0">{i + 1}</Badge>
                      <Input
                        value={item.text}
                        onChange={(e) => {
                          const updated = [...ordItems];
                          updated[i].text = e.target.value;
                          setOrdItems(updated);
                        }}
                        placeholder={`العنصر ${i + 1}`}
                      />
                      {ordItems.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => setOrdItems(ordItems.filter((_, j) => j !== i))}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setOrdItems([...ordItems, { id: String(ordItems.length + 1), text: "" }])}>
                    <Plus className="w-3 h-3 ml-1" /> إضافة عنصر
                  </Button>
                  <p className="text-xs text-muted-foreground">أدخل العناصر بالترتيب الصحيح، وسيتم خلطها للطالب</p>
                </div>
              </>
            )}

            {/* Fill Blank */}
            {type === "fill_blank" && (
              <>
                <div className="space-y-2">
                  <Label>السؤال</Label>
                  <Textarea value={fbQuestion} onChange={(e) => setFbQuestion(e.target.value)} placeholder="مثال: ٥ - ٣ = ___" />
                </div>
                <div className="space-y-2">
                  <Label>الإجابة الصحيحة</Label>
                  <Input value={fbCorrectAnswer} onChange={(e) => setFbCorrectAnswer(e.target.value)} placeholder="٢" />
                </div>
                <div className="space-y-2">
                  <Label>تلميح (اختياري)</Label>
                  <Input value={fbHint} onChange={(e) => setFbHint(e.target.value)} placeholder="فكر في عدد التفاحات المتبقية..." />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 ml-1 animate-spin" />}
              حفظ النشاط
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddActivityDialog;
